import { useCallback, useEffect, useState, useMemo } from "react";
import ReactTableWrapper from "../../components/ReactTableWrapper";
import PremiumPageHeader from "../../components/PremiumPageHeader";
import { ListPageFloatingControls } from "../../components/ListPageLayout";
import { exportAsCSV } from "../../utils/CSVExporter";
import { exportAsPDF } from "../../utils/PDFExporter";
import { openPdfResponse } from "../../utils/openPdfInApp";
import { useNavigate, useSearchParams } from "react-router-dom";
import ListPageGradientSummary from "../../components/ListPageGradientSummary";
import { buildGradientCard, buildCancelledSummaryCard, splitRecordsByStatus } from "../../utils/listSummaryUtils";
import "../../App.css";
import Sidebar from '../../Sidebar';
import Swal from "sweetalert2";
import { useCookies } from 'react-cookie';
import { getAllCubeTestReports, printCubeTestReport } from "../../services/CubeTestReportServices";
import { Row, Col, Button } from "reactstrap";
import { DatePicker } from 'antd';
import moment from 'moment';
import { getUserAllowedPlants } from '../../services/PlantServices';
import { displayError } from "../../helpers";
import { mapAllowedPlants, pickDefaultPlantId } from './cubeTestReportDefaults';

const { RangePicker } = DatePicker;

function CubeTestReportTable() {
    const InitialData = {
        records: []
    };
    const [data, setData] = useState(InitialData);
    const [inputs, setInputs] = useState({});
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [cookies] = useCookies(['myToken']);
    const [isLoading, setIsLoading] = useState(false);
    const [plants, setPlants] = useState([]);
    const initialFromDate = moment().startOf('month');
    const initialToDate = moment();
    const [fromDate, setFromDate] = useState(initialFromDate);
    const [toDate, setToDate] = useState(initialToDate);

    const { active: activeRecords, cancelled: cancelledRecords } = useMemo(
        () => splitRecordsByStatus(data.records),
        [data.records],
    );

    const todayReportCard = useMemo(() => ({
        label: 'Today Reports',
        value: String(activeRecords.filter((row) => row.report_date === moment().format('DD-MM-YYYY')).length),
        gradient: 'linear-gradient(135deg, #b45309 0%, #f59e0b 55%, #fbbf24 100%)',
        shadow: '0 8px 20px rgba(245, 158, 11, 0.28)',
    }), [activeRecords]);

    const summaryCards = useMemo(() => {
        const cards = [
            buildGradientCard('Total', activeRecords.length, 'blue'),
            buildGradientCard('Active', activeRecords.length, 'purple'),
        ];
        const cancelledCard = buildCancelledSummaryCard(cancelledRecords, {
            entityLabel: 'reports cancelled',
        });
        if (cancelledCard) cards.push(cancelledCard);
        return cards;
    }, [activeRecords, cancelledRecords]);

    const cancelledPopup = useMemo(() => ({
        title: `Cancelled Cube Test Reports (${cancelledRecords.length})`,
        emptyMessage: 'No cancelled reports in the current list.',
        columns: [
            { key: 'report_no', label: 'Report No', align: 'center' },
            { key: 'report_date', label: 'Report Date', align: 'center' },
            { key: 'plant_alias', label: 'Plant', align: 'left' },
            { key: 'customer_name', label: 'Customer', align: 'left' },
            { key: 'project_name', label: 'Site', align: 'left' },
            { key: 'grade', label: 'Grade', align: 'left' },
        ],
        getRows: () => cancelledRecords,
        mapRow: (row) => ({
            report_no: row.report_no,
            report_date: row.report_date,
            plant_alias: row.plant_alias,
            customer_name: row.customer_name,
            project_name: row.project_name,
            grade: row.grade,
        }),
        footerRowBuilder: (rows) => ({
            report_no: 'Total',
            report_date: '',
            plant_alias: '',
            customer_name: '',
            project_name: '',
            grade: `${rows.length} reports`,
        }),
    }), [cancelledRecords]);

    const columns = useMemo(
        () => [
            { fieldName: 'plant_alias', headerName: 'Plant' },
            { fieldName: "report_no", headerName: 'Report Number' },
            { fieldName: "report_date", headerName: 'Report Date' },
            { fieldName: "casting_date", headerName: 'Casting Date' },
            { fieldName: "customer_name", headerName: 'Customer Name' },
            { fieldName: "project_name", headerName: 'Site Name' },
            { fieldName: "grade", headerName: 'Grade' },
            { fieldName: 'salesrep_name', headerName: 'Marketing' },
            { fieldName: "status", headerName: 'Status' },
            { fieldName: "id", headerName: "ROW_ACTIONS" }
        ],
        [],
    );

    const handleDateChange = (values) => {
        if (values && values.length === 2) {
            setFromDate(values[0]);
            setToDate(values[1]);
        }
    };

    const onEditCubeTestReport = (ctrId) => {
        navigate(`/CubeTestReportEdit/${ctrId}/`);
    };

    const onPrintCubeTestReport = (ctrId, event) => {
        event.preventDefault();
        setIsLoading(true);
        printCubeTestReport(cookies, ctrId)
            .then((response) => {
                openPdfResponse(response, {
                    title: 'Compressive Strength Report',
                    downloadFileName: 'compressive_strength_report.pdf',
                });
            })
            .catch((error) => {
                displayError(error?.response?.data ?? error, 'Print Failed');
            })
            .finally(() => setIsLoading(false));
    };

    const mapRecords = (response) => (response.cube_test_report_list || []).map((ctr) => ({
        ...ctr,
        plant_alias: ctr.plant?.alias_name || '',
        report_no: `${ctr.prefix || ''}${ctr.report_no || ''}`,
        customer_name: ctr.consignee?.customer?.name || '',
        salesrep_name: ctr.consignee?.customer?.salesrep?.alias
          || ctr.consignee?.customer?.salesrep?.name
          || '',
        consignee_name: ctr.consignee?.name || '',
        project_name: ctr.consignee?.project_name || '',
        status: ctr.status ? 'Active' : 'Cancelled',
    }));

    const loadReports = useCallback(() => {
        if (!inputs.plant_id || !fromDate || !toDate) {
            return;
        }
        setIsLoading(true);
        getAllCubeTestReports(cookies, {
            from_date: fromDate.format('DD-MM-YYYY'),
            upto_date: toDate.format('DD-MM-YYYY'),
            plant_id: inputs.plant_id,
        })
            .then((response) => {
                setData({ records: mapRecords(response) });
            })
            .catch((error) => {
                displayError(error?.response?.data ?? error, 'Failed to load reports');
            })
            .finally(() => setIsLoading(false));
    }, [cookies, fromDate, inputs.plant_id, toDate]);

    const handleCSVExport = () => {
        exportAsCSV(
            columns.filter((col) => col.headerName !== 'ROW_ACTIONS').map((c) => c.headerName),
            data.records.map((ctr) => ({
                plant_alias: ctr.plant_alias,
                report_no: ctr.report_no,
                report_date: ctr.report_date,
                casting_date: ctr.casting_date,
                customer_name: ctr.customer_name,
                project_name: ctr.project_name,
                grade: ctr.grade,
                salesrep_name: ctr.salesrep_name,
                status: ctr.status,
            })),
        );
    };

    const handleChange = (event) => {
        const name = event.target.name;
        setInputs((values) => ({ ...values, [name]: event.target.value }));
    };

    const handlePDFExport = () => {
        exportAsPDF(
            'List of Compressive Strength Reports',
            columns
                .filter((col) => col.headerName !== 'ROW_ACTIONS')
                .map((col) => ({ header: col.headerName, dataKey: col.fieldName })),
            data.records.map((ctr) => ({
                plant_alias: ctr.plant_alias,
                report_no: ctr.report_no,
                report_date: ctr.report_date,
                casting_date: ctr.casting_date,
                customer_name: ctr.customer_name,
                project_name: ctr.project_name,
                grade: ctr.grade,
                salesrep_name: ctr.salesrep_name,
                status: ctr.status,
            })),
            'CompressiveStrengthReports.pdf',
        );
    };

  useEffect(() => {
    const pendingPlantId = searchParams.get('plantId');
    getUserAllowedPlants(cookies)
      .then((plantList) => {
        const plantOptions = mapAllowedPlants(plantList);
        setPlants(plantOptions);
        const defaultPlantId = pendingPlantId || pickDefaultPlantId(plantOptions);
        if (defaultPlantId) {
          setInputs({ plant_id: defaultPlantId });
        }
      });
  }, [cookies, searchParams]);

    const handleSubmit = (event) => {
        event.preventDefault();
        if (inputs.plant_id && fromDate && toDate) {
            loadReports();
        } else {
            Swal.fire('Plant, from date, and to date must not be empty.', '', 'info');
        }
    };

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <div id="page-wrap">
                <ListPageFloatingControls
                    formLink="/CubeTestReport"
                    addNewLabel="New Report"
                    homeLink="/Home"
                    onExportCSV={handleCSVExport}
                    onExportPDF={handlePDFExport}
                />
                <div className="container">
                <PremiumPageHeader
                    module="QUALITY CONTROL"
                    title="List of Compressive Strength Report"
                    subtitle="Filter by plant and date range, then search to load cube test reports."
                    modeLabel="VIEW MODE"
                />
                </div>

                <form className="form" onSubmit={handleSubmit}>
                    <div className="container">
                        <Row>
                            <Col xl={12} lg={12} md={12}>
                                <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 bg-blue form-control-panel">
                                    <label htmlFor="plant_id" className="form-group col-sm-2 text-right">Plant</label>
                                    <select
                                        id="plant_id"
                                        className="browser-default custom-select col-sm-4 mandatory-form-control"
                                        onChange={handleChange}
                                        required
                                        name="plant_id"
                                        value={inputs.plant_id || ''}
                                    >
                                        <option value="">Select Plant Location</option>
                                        {plants.map((item) => (
                                            <option key={item.value} value={item.value}>{item.label}</option>
                                        ))}
                                    </select>
                                    <label className="form-group col-sm-1 text-right">Period</label>
                                    <RangePicker
                                        defaultValue={[initialFromDate, initialToDate]}
                                        format="DD-MM-YYYY"
                                        onChange={handleDateChange}
                                        style={{ height: '40px', width: '270px' }}
                                    />
                                    <div className="col-sm-4" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                                        <Button
                                            type="submit"
                                            style={{
                                                backgroundColor: 'RGB(58,29,238,0.8)',
                                                fontWeight: 'bold',
                                                color: '#fff',
                                                width: '100px',
                                            }}
                                        >
                                            Search
                                        </Button>
                                        <Button
                                            type="button"
                                            style={{ fontWeight: 'bold' }}
                                            onClick={() => {
                                                const plantQuery = inputs.plant_id ? `&plantId=${inputs.plant_id}` : '';
                                                navigate(`/CubeTestReport?pendingAge=7${plantQuery}`);
                                            }}
                                        >
                                            7 Days Pending
                                        </Button>
                                        <Button
                                            type="button"
                                            style={{ fontWeight: 'bold' }}
                                            onClick={() => {
                                                const plantQuery = inputs.plant_id ? `&plantId=${inputs.plant_id}` : '';
                                                navigate(`/CubeTestReport?pendingAge=28${plantQuery}`);
                                            }}
                                        >
                                            28 Days Pending
                                        </Button>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </div>
                </form>

                <ListPageGradientSummary
                    highlightCard={todayReportCard}
                    cards={summaryCards}
                    cancelledPopup={cancelledPopup}
                />

                <div className="container item-list-table-container">
                    <div className="table-responsive">
                        <ReactTableWrapper
                            title="LIST OF COMPRESSIVE STRENGTH REPORT"
                            columns={columns}
                            data={data.records}
                            onRowEdit={onEditCubeTestReport}
                            hideDelete
                            onCSVExport={handleCSVExport}
                            onPDFExport={handlePDFExport}
                            isLoading={isLoading}
                            onRowPrint={onPrintCubeTestReport}
                            hidePrint={false}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CubeTestReportTable;
